import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, UserPlus, Phone, Smartphone, Mail, Globe, MapPin, Share2, Link2 } from 'lucide-react';
import { cardPublicAPI } from '@/lib/cardsAPI';

// The page behind the QR code on a printed business card. One big button saves the contact to the phone.
const telHref = (n) => `tel:${(n || '').replace(/[^\d+]/g, '')}`;

export function CardView({ card, photoSrc, onSave, preview = false }) {
  const accent = card.accent || '#A62B1F';
  const rows = [
    card.phone_mobile && { icon: Smartphone, label: 'Cell', value: card.phone_mobile, href: telHref(card.phone_mobile) },
    card.phone_office && { icon: Phone, label: 'Office', value: card.phone_office, href: telHref(card.phone_office) },
    card.email && { icon: Mail, label: 'Email', value: card.email, href: `mailto:${card.email}` },
    card.website && { icon: Globe, label: 'Website', value: card.website.replace(/^https?:\/\//, '').replace(/\/$/, ''), href: card.website },
    card.address && { icon: MapPin, label: 'Location', value: card.address, href: `https://maps.google.com/?q=${encodeURIComponent(card.address)}` },
  ].filter(Boolean);
  const initials = (card.full_name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="max-w-md mx-auto" style={{ fontFamily: 'Manrope, Arial, sans-serif', color: '#1E2A33' }}>
      <div className="rounded-b-3xl px-6 pt-10 pb-8 text-center" style={{ background: 'linear-gradient(180deg, #4A5262 0%, #9C735E 45%, #D9A066 100%)' }}>
        {photoSrc ? <img src={photoSrc} alt="" className="w-28 h-28 rounded-full mx-auto object-cover border-4" style={{ borderColor: '#F4F0E6' }} />
          : <div className="w-28 h-28 rounded-full mx-auto flex items-center justify-center text-3xl font-semibold border-4" style={{ background: accent, color: '#F4F0E6', borderColor: '#F4F0E6' }}>{initials}</div>}
        <h1 className="mt-4 text-3xl font-semibold" style={{ fontFamily: 'Fraunces, Georgia, serif', color: '#F4F0E6' }}>{card.full_name}</h1>
        {card.title && <div className="mt-1 text-sm font-bold uppercase tracking-wider" style={{ color: '#F4F0E6' }}>{card.title}</div>}
        {card.org && <div className="text-base" style={{ color: '#F4F0E6', opacity: 0.9 }}>{card.org}</div>}
      </div>
      <div className="px-5 -mt-6">
        <a href={preview ? undefined : undefined} onClick={onSave} role="button"
          className="flex items-center justify-center gap-2 w-full h-14 rounded-2xl text-white text-lg font-semibold shadow-md cursor-pointer select-none" style={{ background: accent }}>
          <UserPlus className="w-5 h-5" />Save contact
        </a>
      </div>
      {card.bio && <p className="px-6 pt-5 text-base leading-relaxed text-center whitespace-pre-line" style={{ color: '#33404A' }}>{card.bio}</p>}
      <div className="px-5 pt-5 space-y-2">
        {rows.map((r) => (
          <a key={r.label} href={preview ? undefined : r.href} target={r.label === 'Website' || r.label === 'Location' ? '_blank' : undefined} rel="noopener noreferrer"
            className="flex items-center gap-4 rounded-2xl border bg-white px-4 py-3" style={{ borderColor: '#E3DDD1' }}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: '#F3EFE7' }}><r.icon className="w-5 h-5" style={{ color: accent }} /></span>
            <span className="min-w-0">
              <span className="block text-xs uppercase tracking-wide text-[#5C6670]">{r.label}</span>
              <span className="block text-base font-medium truncate">{r.value}</span>
            </span>
          </a>
        ))}
        {(card.links || []).map((l) => (
          <a key={l.url} href={preview ? undefined : l.url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-4 rounded-2xl border bg-white px-4 py-3" style={{ borderColor: '#E3DDD1' }}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: '#F3EFE7' }}><Link2 className="w-5 h-5" style={{ color: accent }} /></span>
            <span className="text-base font-medium">{l.label}</span>
          </a>
        ))}
      </div>
      {!preview && typeof navigator !== 'undefined' && navigator.share && (
        <div className="px-5 pt-3">
          <button onClick={() => navigator.share({ title: card.full_name, url: window.location.href }).catch(() => {})}
            className="flex items-center justify-center gap-2 w-full h-12 rounded-2xl border text-base font-semibold bg-white" style={{ borderColor: '#E3DDD1' }}>
            <Share2 className="w-4 h-4" />Share this card
          </button>
        </div>
      )}
      <div className="text-center text-xs text-[#5C6670] py-8">Made with Stagehead</div>
    </div>
  );
}

export default function PublicCard() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    cardPublicAPI.get(slug).then((r) => { setCard(r.data); document.title = r.data.full_name; }).catch(() => setMissing(true));
  }, [slug]);
  const page = (c) => <div className="min-h-screen" style={{ background: '#FBF8F2' }}>{c}</div>;
  if (missing) return page(<div className="max-w-md mx-auto p-8 text-center text-[#5C6670]">This card isn't available.</div>);
  if (!card) return page(<div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin" /></div>);
  // A plain link to the .vcf: iPhone opens "Add to Contacts", Android saves it and opens it in Contacts.
  return page(<CardView card={card} photoSrc={card.has_photo ? cardPublicAPI.photoUrl(card.slug) : null}
    onSave={() => { window.location.href = cardPublicAPI.vcardUrl(card.slug); }} />);
}
